import React, { useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SupaHelpFlow from '../components/supahelp/SupaHelpFlow';
import { ChevronDown, ChevronUp, HelpCircle, Clock, FileText, Wrench, Zap, MessageCircle } from 'lucide-react';

// WhatsApp configuration
const WHATSAPP_NUMBER = '61478453548';
const WHATSAPP_MESSAGE = "Hi Supa Energy Hub! I'd like to chat about...";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden">
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
    >
      <span className="font-medium text-[#3D5A47]">{question}</span>
      {isOpen ? (
        <ChevronUp className="w-5 h-5 text-[#B5D43B] flex-shrink-0" />
      ) : (
        <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
      )}
    </button>
    {isOpen && (
      <div className="px-4 pb-4 text-gray-600">
        {answer}
      </div>
    )}
  </div>
);

const SupportPage = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqs = [
    {
      question: "How quickly will you respond?",
      answer: "For existing customers with urgent issues, we aim to respond within 2-4 business hours. For general enquiries and quotes, we typically respond within 24 hours. Priority is always given to existing customers experiencing system issues."
    },
    {
      question: "What information helps us resolve issues faster?",
      answer: "For support requests, please include: your installation address, a description of the issue, when you first noticed it, any error messages or lights on your inverter, and photos if possible. This helps our team diagnose and resolve your issue more quickly."
    },
    {
      question: "What does the warranty cover?",
      answer: "Your warranty covers manufacturing defects in panels, inverters, and batteries. Most panels have 25-year performance warranties, inverters typically have 5-10 year warranties, and batteries usually have 10-year warranties. Labour warranties vary by installation. Contact us for your specific warranty details."
    },
    {
      question: "Can I upgrade my existing system?",
      answer: "Yes! Many customers add batteries to existing solar systems, or upgrade to larger inverters. We can also add EV chargers, heat pumps, or additional panels. Just select 'Upgrade options' in the form above and we'll assess your current system."
    },
    {
      question: "How do I monitor my system's performance?",
      answer: "Most systems include monitoring apps where you can track generation, consumption, and battery levels in real-time. If you're having trouble with your monitoring app, select 'Monitoring / app issue' in the support form above."
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-[#3D5A47] to-[#2d4435] text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Supa Help</h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              One place for advice, quotes, and customer support.
              <br />
              <span className="text-[#B5D43B]">Existing customers get priority support.</span>
            </p>
          </div>
        </section>

        {/* Quick Info Cards */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
          <div className="grid md:grid-cols-4 gap-4">
            {[
              { icon: Clock, title: "Fast Response", desc: "2-4 hours for urgent issues" },
              { icon: Wrench, title: "Expert Support", desc: "Accredited technicians" },
              { icon: FileText, title: "Free Quotes", desc: "No obligation estimates" },
              { icon: Zap, title: "Full Service", desc: "Solar, battery, EV, heat pumps" },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl shadow-lg p-5 text-center">
                <div className="w-12 h-12 bg-[#B5D43B]/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <item.icon className="w-6 h-6 text-[#B5D43B]" />
                </div>
                <h3 className="font-semibold text-[#3D5A47] mb-1">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Main Content */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Form Section */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-gray-100">
                <SupaHelpFlow source="support_page" isFullPage={true} />
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2">
              {/* Contact Info */}
              <div className="bg-gray-50 rounded-2xl p-6 mb-8">
                <h3 className="font-semibold text-[#3D5A47] mb-4 flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-[#B5D43B]" />
                  Need immediate help?
                </h3>
                <div className="space-y-3 text-sm">
                  <p className="text-gray-600">
                    For urgent system issues, you can also reach us directly:
                  </p>
                  <p className="font-medium text-[#3D5A47]">
                    <a href="tel:0478453548" className="hover:text-[#B5D43B]">
                      📞 0478 453 548
                    </a>
                  </p>
                  <p className="font-medium text-[#3D5A47]">
                    <a href="mailto:hello@supaenergyhub.com.au" className="hover:text-[#B5D43B]">
                      ✉️ hello@supaenergyhub.com.au
                    </a>
                  </p>
                  
                  {/* WhatsApp Button */}
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full p-3 mt-4 rounded-xl bg-[#25D366] hover:bg-[#20bd5a] text-white font-medium transition-all"
                  >
                    <WhatsAppIcon className="w-5 h-5" />
                    <span>Chat on WhatsApp</span>
                  </a>
                  
                  <p className="text-xs text-gray-500 mt-4">
                    Business hours: Mon-Fri 8am-6pm AEST
                  </p>
                </div>
              </div>

              {/* FAQs */}
              <div>
                <h3 className="font-semibold text-[#3D5A47] mb-4">Frequently Asked Questions</h3>
                <div className="space-y-3">
                  {faqs.map((faq, i) => (
                    <FAQItem
                      key={i}
                      question={faq.question}
                      answer={faq.answer}
                      isOpen={openFAQ === i}
                      onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SupportPage;
